/**
 * 💰 FINANCIAL MANAGEMENT HUB
 * Consolidated payment management for drivers, hosts, refunds, and financial analytics
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  Alert,
} from '@mui/material';
import {
  AccountBalance,
  Business,
  Refresh as RefundIcon,
  TrendingUp,
  Star,
  Sync,
  AttachMoney,
} from '@mui/icons-material';
import { DriverPayoutsTab } from './tabs/DriverPayoutsTab';
import { HostPayoutsTab } from './tabs/HostPayoutsTab';
import { RefundsTab } from './tabs/RefundsTab';
import { FinancialOverviewTab } from './tabs/FinancialOverviewTab';
import { TipsTab } from './tabs/TipsTab';
import { PaymentSyncTab } from './tabs/PaymentSyncTab';
import { HostEarningsOverview } from '@/pages/admin/tabs/HostEarningsOverview';
import { HostEarningsBreakdown } from '@/pages/admin/tabs/HostEarningsBreakdown';
import { DriverEarningsOverview } from '@/pages/admin/tabs/DriverEarningsOverview';
import { DriverEarningsBreakdown } from '@/pages/admin/tabs/DriverEarningsBreakdown';

const TABS = ['overview', 'driver-payouts', 'host-payouts', 'driver-earnings', 'host-earnings', 'refunds', 'tips', 'payment-sync'] as const;
type TabValue = typeof TABS[number];

export default function FinancialManagementPage() {
  // The tab is in the URL (?tab=host-payouts) so links — e.g. from admin alert emails — open it
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabValue | null;
  const activeTab: TabValue = tabParam && TABS.includes(tabParam) ? tabParam : 'driver-payouts';
  const setActiveTab = (tab: TabValue) => setSearchParams(params => { params.set('tab', tab); return params; }, { replace: true });
  const [hostEarningsSubTab, setHostEarningsSubTab] = useState<'overview' | 'breakdown'>('overview');
  const [driverEarningsSubTab, setDriverEarningsSubTab] = useState<'overview' | 'breakdown'>('overview');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          💰 Financial Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage payouts, refunds, tips, and financial analytics across all stakeholders
        </Typography>
      </Box>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
            },
          }}
        >
          <Tab
            value="overview"
            label="Financial Overview"
            icon={<TrendingUp />}
            iconPosition="start"
          />
          <Tab
            value="driver-payouts"
            label="Driver Payouts"
            icon={<AccountBalance />}
            iconPosition="start"
          />
          <Tab
            value="host-payouts"
            label="Host/Fleet Owner Payouts"
            icon={<Business />}
            iconPosition="start"
          />
          <Tab
            value="driver-earnings"
            label="Driver Earnings Analytics"
            icon={<AttachMoney />}
            iconPosition="start"
          />
          <Tab
            value="host-earnings"
            label="Host Earnings Analytics"
            icon={<AttachMoney />}
            iconPosition="start"
          />
          <Tab
            value="refunds"
            label="Refunds"
            icon={<RefundIcon />}
            iconPosition="start"
          />
          <Tab
            value="tips"
            label="Tips Management"
            icon={<Star />}
            iconPosition="start"
          />
          <Tab
            value="payment-sync"
            label="Payment Sync"
            icon={<Sync />}
            iconPosition="start"
          />
        </Tabs>
      </Card>

      {/* Tab Content */}
      <Box>
        {activeTab === 'overview' && <FinancialOverviewTab />}
        {activeTab === 'driver-payouts' && <DriverPayoutsTab />}
        {activeTab === 'host-payouts' && <HostPayoutsTab />}
        {activeTab === 'driver-earnings' && (
          <Box>
            {/* Sub-tabs for Driver Earnings */}
            <Card sx={{ mb: 3 }}>
              <Tabs
                value={driverEarningsSubTab}
                onChange={(_e, v) => setDriverEarningsSubTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
              >
                <Tab value="overview" label="Overview & Top Earners" />
                <Tab value="breakdown" label="Detailed Breakdown" />
              </Tabs>
            </Card>
            {driverEarningsSubTab === 'overview' && <DriverEarningsOverview />}
            {driverEarningsSubTab === 'breakdown' && <DriverEarningsBreakdown />}
          </Box>
        )}
        {activeTab === 'host-earnings' && (
          <Box>
            {/* Sub-tabs for Host Earnings */}
            <Card sx={{ mb: 3 }}>
              <Tabs
                value={hostEarningsSubTab}
                onChange={(_e, v) => setHostEarningsSubTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
              >
                <Tab value="overview" label="Overview & Top Earners" />
                <Tab value="breakdown" label="Detailed Breakdown" />
              </Tabs>
            </Card>
            {hostEarningsSubTab === 'overview' && <HostEarningsOverview />}
            {hostEarningsSubTab === 'breakdown' && <HostEarningsBreakdown />}
          </Box>
        )}
        {activeTab === 'refunds' && <RefundsTab />}
        {activeTab === 'tips' && <TipsTab />}
        {activeTab === 'payment-sync' && <PaymentSyncTab />}
      </Box>
    </Box>
  );
}
